/**
 * Cheap local pre-read of a dropped file (plan §4.2).
 *
 * Runs in the browser while the upload starts so the staging row can show
 * "Fresha · 1,240" the moment the file lands, instead of a spinner until the
 * server's extraction finishes. The server's `file.analysis` replaces this
 * when it arrives; nothing here is authoritative.
 *
 * CSV only for now (header sniff + row count on the first 256 KB, extrapolated
 * by size). Spreadsheets, PDFs and photos get a kind-only guess.
 */

// Header signatures of the exports we expect to see most. Order matters:
// first match wins. Columns are lower-cased, trimmed.
const SIGNATURES = [
  { source: 'fresha', kinds: ['clients'], all: ['first name', 'last name'], any: ['mobile', 'accepts marketing', 'client since', 'blocked'] },
  { source: 'fresha', kinds: ['bookings'], all: ['appointment'], any: ['team member', 'ref #', 'booking date'] },
  { source: 'treatwell', kinds: ['clients'], any: ['treatwell', 'customer name', 'customer email'] },
  { source: 'phorest', kinds: ['clients'], any: ['clientid', 'phorest', 'client no'] },
  { source: 'booksy', kinds: ['clients'], any: ['booksy'] },
  { source: 'vagaro', kinds: ['clients'], any: ['vagaro'] },
  { source: 'square', kinds: ['clients'], any: ['square customer id', 'reference id'] },
  { source: 'timely', kinds: ['clients'], any: ['timely'] },
  { source: 'ovatu', kinds: ['clients'], any: ['ovatu'] },
];

// A column that IS appointment-shaped ("Appointment date", "Booking ref",
// "Start time"), anchored at the start so a client export's "Last appointment"
// or "Total bookings" column can't drag the whole file into bookings — that
// made client uploads flash as Bookings until the server analysis corrected it.
const BOOKING_COL = /^(appointment|booking)\s*(date|time|ref|id|status)|^(start|end)\s*time\b|^ref\s*#/;

const KIND_HINTS = [
  { kinds: ['bookings'], test: (header) => header.some((h) => BOOKING_COL.test(h)) },
  { kinds: ['services'], any: ['service', 'duration', 'price', 'treatment'] },
  { kinds: ['products'], any: ['product', 'sku', 'barcode', 'retail', 'stock'] },
  { kinds: ['staff'], any: ['employee', 'staff', 'team member', 'stylist'] },
  { kinds: ['clients'], any: ['email', 'phone', 'mobile', 'client', 'customer', 'first name', 'surname'] },
];

function splitCsvLine(line) {
  const out = [];
  let cur = '';
  let q = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') {
      if (q && line[i + 1] === '"') { cur += '"'; i++; } else q = !q;
    } else if ((c === ',' || c === ';' || c === '\t') && !q) {
      out.push(cur); cur = '';
    } else cur += c;
  }
  out.push(cur);
  return out.map((s) => s.trim().replace(/^﻿/, '').toLowerCase());
}

function readHead(file, bytes) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => resolve('');
    reader.readAsText(file.slice(0, bytes));
  });
}

export function kindFromName(file) {
  const n = (file.name || '').toLowerCase();
  const t = (file.type || '').toLowerCase();
  if (t.startsWith('image/') || /\.(jpe?g|png|heic|webp|gif)$/.test(n)) return { kind: 'photo', isDoc: false };
  if (t === 'application/pdf' || /\.pdf$/.test(n)) return { kind: 'pdf', isDoc: true };
  if (/\.(xlsx|xls|numbers|ods)$/.test(n)) return { kind: 'spreadsheet', isDoc: true };
  if (/\.csv$|text\/csv/.test(n + ' ' + t)) return { kind: 'csv', isDoc: true };
  return { kind: 'file', isDoc: true };
}

/**
 * @returns {{ source: string|null, kinds: string[], rows: number|null, tag: string }}
 */
export async function prereadFile(file) {
  const { kind } = kindFromName(file);
  if (kind !== 'csv') {
    const guess = kind === 'photo' ? 'Photo' : kind === 'pdf' ? 'PDF' : kind === 'spreadsheet' ? 'Spreadsheet' : 'File';
    return { source: null, kinds: [], rows: null, tag: guess };
  }
  const headText = await readHead(file, 256 * 1024);
  if (!headText) return { source: null, kinds: [], rows: null, tag: 'CSV' };
  const lines = headText.split(/\r?\n/).filter((l) => l.trim().length);
  if (!lines.length) return { source: null, kinds: [], rows: null, tag: 'CSV' };
  const header = splitCsvLine(lines[0]);
  const has = (col) => header.some((h) => h.includes(col));

  let source = null;
  let kinds = [];
  for (const sig of SIGNATURES) {
    const allOk = !sig.all || sig.all.every(has);
    const anyOk = !sig.any || sig.any.some(has);
    if (allOk && anyOk) { source = sig.source; kinds = sig.kinds; break; }
  }
  if (!kinds.length) {
    const hint = KIND_HINTS.find((k) => (k.test ? k.test(header) : k.any.some(has)));
    if (hint) kinds = hint.kinds;
  }

  // Row count: exact if the whole file fit in the head, otherwise extrapolate.
  const dataLines = Math.max(lines.length - 1, 0);
  const sampledBytes = Math.min(file.size, 256 * 1024);
  const rows = file.size > sampledBytes && sampledBytes > 0
    ? Math.round(dataLines * (file.size / sampledBytes))
    : dataLines;

  const sourceLabel = source ? source.charAt(0).toUpperCase() + source.slice(1) : null;
  const rowsLabel = rows ? rows.toLocaleString('en-GB') : null;
  const kindLabel = kinds[0] || 'rows';
  const tag = sourceLabel && rowsLabel ? `${sourceLabel} · ${rowsLabel}` : rowsLabel ? `${rowsLabel} ${kindLabel}` : (sourceLabel || 'CSV');
  return { source, kinds, rows, tag };
}
